import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Typography, IconButton, Input } from '@material-tailwind/react';
import { debounce } from 'lodash';
import { getReferalHistoryIndex } from '../../util/refer-api';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/solid';
import ChatButton from '../../components/Chat/ChatButton';
import { formatAddress } from '../../util/format-data';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import alkanesImg from '../../assets/imgs/alkanes.png';
import cheekyImg from '../../assets/imgs/cheeky.png';

export default function LeaderboardAlkanes() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [datas, setDatas] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchData(query, page);
  }, [page]);

  const fetchData = async (value, page) => {
    setLoading(true);
    const response = await getReferalHistoryIndex(value, page, limit);
    setDatas(response.data);
    setTotal(Math.ceil(response.total / limit));
    setLoading(false);
  }

  const next = () => {
    if (page === total) return;
    setPage(page + 1);
  };
 
  const prev = () => {
    if (page === 1) return;
    setPage(page - 1);
  };

  const doublePrev = () => {
    setPage(1);
  }

  const doubleNext = () => {
    setPage(total);
  }

  const debouncedSearch = useCallback(debounce( async (value, page) => {
    try {
      fetchData(value, page);
    }
    catch (e) {
      console.log(e);
    }
  }, 500), []); 

  const handleChange = (e) => {
    setQuery(e.target.value);
    debouncedSearch(e.target.value, page);
  }
  
  const handleCopy = (value, mode) => {
    navigator.clipboard.writeText(value).then(
      () => {
        // Successfully copied to clipboard
        if (mode == 1)
          toast.success(t('Name Copied!'));
        else
          toast.success(t('Address Copied!'));
      },
      (err) => {
        // Failed to copy to clipboard
        console.error('Could not copy address: ', err);
      }
    );
  }

  return (
    <div className="text-white mt-[65px] w-full max-w-[1000px] md:px-7 px-2 flex flex-col justify-center items-center mb-10 min-h-[600px]">
      {
        loading ? <Spinner className="h-16 w-16 text-gray-900/50 fixed" color="pink"/> : <></>
      }
      <div className="flex flex-row justify-center gap-1 items-center w-full mt-10">
        <img src={alkanesImg} className="w-[50px] h-[50px] rounded-full"/>
        <div className=" text-primary sm:text-3xl text-2xl font-bold text-center">
          {t('Top Referrers')}
        </div>
        <img src={cheekyImg} className="w-[50px] h-[50px] rounded-full"/>
      </div>
      <div className="flex flex-row justify-end w-full mt-10">
        <div className="flex flex-row items-center gap-2">
          <Input type="text" color="white" label = {t('Search By Address')} className="w-[250px]" value = {query} onChange = {(e) => {handleChange(e)}}/>
        </div>
      </div>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex flex-row px-4 py-2 w-full">
          <div className = "w-[40%]">{t('Address/Name')}</div>
          <div className = "w-[30%]">{t('Users')}</div>
          <div className = "w-[30%]">{t('Sats Earned')}</div>
        </div>
        {
          datas.map((data) => 
            <div 
              className="flex flex-row items-center text-white bg-gray-900 p-4 rounded-lg cursor-pointer w-full" 
              key={uuidv4()}
            >
              {
                data.domain != "" && data.domain != null ? 
                <div className="w-[40%] text-primary flex flex-row gap-1">
                  <div className="cursor-pointer text-primary" onClick={() => handleCopy(data.address, 2)}>
                    {data.domain}
                  </div>
                  <ChatButton targetAddress = {data.address}/>
                </div>
                :
                <div className = "w-[40%] cursor-pointer" onClick={() => handleCopy(data.address, 2)}>{formatAddress(data.address)}</div>
              }
              <div className = "w-[30%]">
                {data.totalCount}
              </div>
              <div className = "w-[30%]">
                {data.totalSats}
              </div>
            </div>
          )
        }
      </div>
      {
        total > 1 ? 
        <div className="flex flex-row items-center gap-8 justify-end mt-5">
          <div className="flex flex-row items-center gap-2">
            <IconButton
              size="sm"
              variant="outlined"
              onClick={doublePrev}
              disabled={page === 1}
              className="bg-primary hover:bg-primary-hover"
            >
              <ChevronDoubleLeftIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
            <IconButton
              size="sm"
              variant="outlined"
              onClick={prev}
              disabled={page === 1}
              className="bg-primary hover:bg-primary-hover"
            >
              <ChevronLeftIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
          </div>
          <Typography color="white" className="font-normal">
            {t('Page')} <strong className="text-primary">{page}</strong> of{" "}
            <strong className="text-primary">{total}</strong>
          </Typography>
          <div className="flex flex-row items-center gap-2">
            <IconButton
              size="sm"
              variant="outlined"
              onClick={next}
              disabled={page === total}
              className="bg-primary hover:bg-primary-hover"
            >
              <ChevronRightIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
            <IconButton
              size="sm"
              variant="outlined"
              onClick={doubleNext}
              disabled={page === total}
              className="bg-primary hover:bg-primary-hover"
            >
              <ChevronDoubleRightIcon strokeWidth={2} className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
        :
        <></>
      }
    </div>
  )
}